import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, getDocs, updateDoc, deleteDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { setDirtyFlag } from '../safeNavigate';

import PageWrapperAuto from '../components/PageWrapperAuto';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmLeaveModal from '../components/ConfirmLeaveModal';
import ActionConfirmModal from '../components/ActionConfirmModal';
import CustomTagInput from '../components/CustomTagInput';
import { useUserPrefs } from '../UserPrefsProvider';
import { normalizeTagsArray, normalizeTag, normalizeKey, normalizeType, keyRoot, normalizeGenre, getNormalizedGenreOptions } from '../utils/normalize';
import { flushPendingWrites, addPendingWrite } from '../utils/flushPendingWrites';
import { getModeLabelsForEntity } from '../utils/modeLabels';
import dexie from '../offlineDB';

import { useActiveSession } from '../ActiveSessionContext';

export default function EditSessionPage() {
  const { inputStyle } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();

  const [location, setLocation] = useState('');

  const [notes, setNotes] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [sessionItems, setSessionItems] = useState([]);
  const [wasCreatedEmpty, setWasCreatedEmpty] = useState(false);
  const [customTags, setCustomTags] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [initialSessionItems, setInitialSessionItems] = useState([]);
  const [allTunes, setAllTunes] = useState([]);
  const [allSets, setAllSets] = useState([]);
  const [addMode, setAddMode] = useState('tunes');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
  const [saveErrorModalOpen, setSaveErrorModalOpen] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
const { prefs } = useUserPrefs();
const getCollectionBadge = (entityMode) => (
  (getModeLabelsForEntity(prefs?.appMode || 'tunes', entityMode || 'tunes')?.Collection || 'Set').toUpperCase()
);
const getCollectionBadgeColor = (entityMode) => (
  getCollectionBadge(entityMode) === 'FOLDER' ? '#d97706' : '#228be6'
);
const { dexieReady } = useUI();
const { activeSession } = useActiveSession();
const itemKey = (item) => `${item._entryType || 'tune'}:${item.id}`;

useEffect(() => {
  // Prevent tune-only filters from hiding sets when switching add mode.
  setFilterType('');
  setFilterGenre('');
  setFilterKey('');
  setSearch('');
  setSearchQuery('');
}, [addMode]);

const toInputFormat = val => {
  let date;

  if (val?.seconds != null) {
    date = new Date(val.seconds * 1000);
  } else if (val?.toDate) {
    date = val.toDate();
  } else {
    date = new Date(val);
  }

  if (isNaN(date)) return '';

  // Convert to local datetime string: YYYY-MM-DDTHH:MM
  const pad = n => n.toString().padStart(2, '0');
  const local = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return local;
};




  useEffect(() => {
    if (!user) return;

    const unsubTunes = onSnapshot(collection(db, 'users', user.uid, 'tunes'), snapshot => {
      setAllTunes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubSets = onSnapshot(collection(db, 'users', user.uid, 'sets'), snapshot => {
      setAllSets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const load = async () => {
      if (navigator.onLine) await flushPendingWrites(user);
      const local = await dexie.sessions.get(sessionId);
      const snap = !local && await getDoc(doc(db, 'users', user.uid, 'sessions', sessionId));
      const data = local || (snap?.exists() ? snap.data() : null);
console.log('[Session load] Raw startedAt:', data.startedAt);
console.log('[Session load] Raw endedAt:', data.endedAt);
console.log('[Session load] Raw tunes array:', data.tunes);

      if (!data) return;

setLocation(data.location || '');
      setNotes(data.notes || '');
      setStartedAt(toInputFormat(data.startedAt));
      setEndedAt(toInputFormat(data.endedAt));
      console.log('[Session load] Formatted startedAt:', toInputFormat(data.startedAt));
console.log('[Session load] Formatted endedAt:', toInputFormat(data.endedAt));

let linkedTunes = [];
let linkedSets = [];

// Try to pull from Dexie first (offline support)
if (dexieReady) {
  const cachedTunes = await dexie.tunes.toArray();
  const cachedSets = await dexie.sets.toArray();
  linkedTunes = cachedTunes.filter(t =>
    Array.isArray(t.sessions) &&
    t.sessions.some(s => s.sessionId === sessionId)
  );
  linkedSets = cachedSets.filter(s =>
    Array.isArray(s.sessions) &&
    s.sessions.some(x => x.sessionId === sessionId)
  );
}

// Fallback to Firestore if nothing loaded from Dexie
if (linkedTunes.length === 0 && linkedSets.length === 0 && user) {
  const [tunesSnap, setsSnap] = await Promise.all([
    getDocs(collection(db, 'users', user.uid, 'tunes')),
    getDocs(collection(db, 'users', user.uid, 'sets'))
  ]);
  const allTunesFS = tunesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const allSetsFS = setsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  linkedTunes = allTunesFS.filter(t =>
    Array.isArray(t.sessions) &&
    t.sessions.some(s => s.sessionId === sessionId)
  );
  linkedSets = allSetsFS.filter(s =>
    Array.isArray(s.sessions) &&
    s.sessions.some(x => x.sessionId === sessionId)
  );
}

setSessionItems([
  ...linkedTunes.map(t => ({ ...t, _entryType: 'tune' })),
  ...linkedSets.map(s => ({ ...s, _entryType: 'set' }))
]);
setInitialSessionItems([
  ...linkedTunes.map(t => ({ ...t, _entryType: 'tune' })),
  ...linkedSets.map(s => ({ ...s, _entryType: 'set' }))
]);



setCustomTags(data.customTags || []);
if (!data.location && (!data.tunes || data.tunes.length === 0)) setWasCreatedEmpty(true);
    };

    load();
    return () => {
      unsubTunes();
      unsubSets();
    };
  }, [user, sessionId]);

  const handleSave = async () => {
    if (!user) return;

    const updated = {
  location: location.trim() || 'Unnamed',
      notes: notes.trim(),
      startedAt: startedAt ? new Date(startedAt) : null,
      endedAt: endedAt ? new Date(endedAt) : null,
      tunes: sessionItems.map(item => ({
        id: item.id,
        title: item.title || '',
        type: item._entryType === 'set' ? 'set' : (item.type || ''),
        key: item.key || ''
      })),
      customTags: normalizeTagsArray(customTags),
      updatedAt: new Date().toISOString()
    };

    const ref = doc(db, 'users', user.uid, 'sessions', sessionId);
    const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');

    try {
      const snap = await getDoc(prefsRef);
      const oldTags = snap.exists() ? snap.data().customTags || [] : [];
      const mergedTags = Array.from(new Set([
        ...normalizeTagsArray(customTags),
        ...oldTags.map(normalizeTag)
      ]));

      if (navigator.onLine) {
        await updateDoc(prefsRef, { customTags: mergedTags });
        await updateDoc(ref, updated);
      } else {
        await addPendingWrite({
          id: 'settings',
          type: 'update',
          collection: 'preferences',
          docId: 'settings',
          data: { customTags: mergedTags }
        });
        await addPendingWrite({
          id: sessionId,
          type: 'update',
          collection: 'sessions',
          docId: sessionId,
          data: { id: sessionId, ...updated }
        });
      }

 await dexie.sessions.put({ id: sessionId, ...updated });

// Also update each linked item (tune or set) to reflect this session
for (const item of sessionItems) {
  const collectionName = item._entryType === 'set' ? 'sets' : 'tunes';
  const ref = doc(db, 'users', user.uid, collectionName, item.id);

  let original;
  if (navigator.onLine) {
    const snap = await getDoc(ref);
    if (snap.exists()) {
      original = snap.data();
    }
  } else {
    original = collectionName === 'sets'
      ? await dexie.sets.get(item.id)
      : await dexie.tunes.get(item.id);
  }

  if (!original) continue;

  const existingSessions = original.sessions || [];
  const alreadyLinked = existingSessions.some(s => s.sessionId === sessionId);
  if (alreadyLinked) continue;

  const updatedSessions = [
    ...existingSessions,
    {
      sessionId,
      location: location.trim() || 'Unnamed',
      startedAt: startedAt ? new Date(startedAt).toISOString() : new Date().toISOString()
    }
  ];

  if (navigator.onLine) {
    await updateDoc(ref, { sessions: updatedSessions });
  }

  if (collectionName === 'sets') {
    await dexie.sets.update(item.id, { sessions: updatedSessions });
  } else {
    await dexie.tunes.update(item.id, { sessions: updatedSessions });
  }
  await addPendingWrite({
    id: `${item.id}__sessionAdd`,
    type: 'update',
    collection: collectionName,
    docId: item.id,
    data: { sessions: updatedSessions }
  });
}

// Remove this session link from items that were removed in editor
const currentKeys = new Set(sessionItems.map(itemKey));
const removedItems = initialSessionItems.filter(item => !currentKeys.has(itemKey(item)));
for (const item of removedItems) {
  const collectionName = item._entryType === 'set' ? 'sets' : 'tunes';
  const ref = doc(db, 'users', user.uid, collectionName, item.id);

  let original;
  if (navigator.onLine) {
    const snap = await getDoc(ref);
    original = snap.exists() ? snap.data() : null;
  } else {
    original = collectionName === 'sets'
      ? await dexie.sets.get(item.id)
      : await dexie.tunes.get(item.id);
  }
  if (!original) continue;

  const updatedSessions = (original.sessions || []).filter(s => s.sessionId !== sessionId);
  if (navigator.onLine) {
    await updateDoc(ref, { sessions: updatedSessions });
  }
  if (collectionName === 'sets') {
    await dexie.sets.update(item.id, { sessions: updatedSessions });
  } else {
    await dexie.tunes.update(item.id, { sessions: updatedSessions });
  }
  await addPendingWrite({
    id: `${item.id}__sessionRemove`,
    type: 'update',
    collection: collectionName,
    docId: item.id,
    data: { sessions: updatedSessions }
  });
}

setIsDirty(false);
setDirtyFlag(false);
setInitialSessionItems(sessionItems.map(item => ({ ...item })));
navigate(`/session/${sessionId}`, { replace: true });

    } catch (err) {
      console.error('Error saving session:', err);
      setSaveErrorMessage(err?.message ? `Failed to save session: ${err.message}` : 'Failed to save session.');
      setSaveErrorModalOpen(true);
    }
  };

  const handleRemove = (id, entryType) => {
    setSessionItems(prev => prev.filter(t => !(t.id === id && t._entryType === entryType)));
    setIsDirty(true);
  };

  const handleDragEnd = result => {
    if (!result.destination) return;
    const reordered = Array.from(sessionItems);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setSessionItems(reordered);
    setIsDirty(true);
  };

  const sourceItems = addMode === 'sets' ? allSets.filter(s => !s.isProgram) : allTunes;

  const keyGroups = {};
  allTunes.forEach(t => {
    const nk = normalizeKey(t.key);
    if (!nk) return;
    const root = keyRoot(nk);
    if (!keyGroups[root]) keyGroups[root] = new Set();
    keyGroups[root].add(nk);
  });

  const genreOptions = getNormalizedGenreOptions(sourceItems.map((t) => t.genre));
  const typeOptions = [...new Set(sourceItems.map(t => normalizeType(t.type)).filter(Boolean))].sort();
  const filtered = sourceItems.filter(t => {
    const q = search.toLowerCase();
    const nk = normalizeKey(t.key || '');
    return (!searchQuery || Object.values(t).some(val => typeof val === 'string' && val.toLowerCase().includes(searchQuery.toLowerCase())))
      && (!q || t.title?.toLowerCase().includes(q))
      && (!filterType || normalizeType(t.type) === normalizeType(filterType))
      && (addMode === 'sets' || !filterKey || nk === filterKey || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', '')))
      && (!filterGenre || normalizeGenre(t.genre) === filterGenre);
  });

  useEffect(() => {
    const handler = e => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useEffect(() => setDirtyFlag(isDirty), [isDirty]);
  useEffect(() => () => { setIsDirty(false); setDirtyFlag(false); }, []);

  const formatTuneLabel = t => {
    const parts = [];
    if (t.type) parts.push(t.type);
    if (t.key) parts.push(t.key);
    return parts.length ? ` (${parts.join(', ')})` : '';
  };

  console.log('[Render] startedAt state:', startedAt);
console.log('[Render] endedAt state:', endedAt);

  return (
    <PageWrapperAuto>

        {activeSession?.id === sessionId && (
  <div style={{
    background: 'var(--import-bg)',
    padding: '10px',
    marginBottom: '16px',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--text-muted)',
    fontStyle: 'italic'
  }}>
    You are currently editing the active session.
  </div>
)}

      <h2>Edit Session</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px', margin: '0 auto 20px' }}>
<input
  style={inputStyle}
  value={location}
  onChange={e => { setLocation(e.target.value); setIsDirty(true); }}
  placeholder="Session location"
/>
        <textarea style={inputStyle} value={notes} onChange={e => { setNotes(e.target.value); setIsDirty(true); }} placeholder="Notes (optional)" rows={3} />
        <input style={inputStyle} type="datetime-local" value={startedAt} onChange={e => { setStartedAt(e.target.value); setIsDirty(true); }} />
<input
  style={inputStyle}
  type="datetime-local"
  value={endedAt}
  onChange={e => { setEndedAt(e.target.value); setIsDirty(true); }}
  disabled={activeSession?.id === sessionId}
/>
        <div>
          <h4 style={{ marginBottom: '0.25rem' }}>Tags</h4>
          <CustomTagInput tags={customTags} setTags={tags => { setCustomTags(tags); setIsDirty(true); }} suggestions={prefs?.customTags || []} inputStyle={inputStyle} />
        </div>
      </div>

      <h3>Session Items</h3>
<DragDropContext onDragEnd={handleDragEnd}>
  <Droppable droppableId="tuneList">
    {(provided) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        style={{
          maxHeight: '200px',
          overflowY: 'auto',
          border: 'none',
          borderRadius: '8px',
          padding: '6px',
          backgroundColor: 'var(--input-bg)',
          color: 'inherit',
          minHeight: '40px'
        }}
      >
        {sessionItems.map((t, index) => (
          <Draggable key={`${t._entryType || 'tune'}-${t.id || t.title}-${index}`} draggableId={`${t._entryType || 'tune'}-${t.id || t.title}-${index}`} index={index}>
            {(provided) => {
              const style = {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--item-bg, #f9f9f9)',
                color: 'var(--text-color, #000)',
                padding: '8px',
                borderBottom: '1px solid #ccc',
                ...provided.draggableProps.style
              };
              return (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  style={style}
                >
                  <span className="text-scaled">
                    {t.title}{formatTuneLabel(t)}
                    {t._entryType === 'set' && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '0.68rem',
                        backgroundColor: getCollectionBadgeColor(t.mode || 'tunes'),
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                        {getCollectionBadge(t.mode || 'tunes')}
                      </span>
                    )}
                  </span>
                  <button onClick={() => handleRemove(t.id, t._entryType || 'tune')} style={{ ...inputStyle, marginLeft: '10px' }}>×</button>
                </div>
              );
            }}
          </Draggable>
        ))}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
</DragDropContext>


      <h3>Add Items</h3>
<div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
  <button
    className="btn-subtle"
    onClick={() => setAddMode('tunes')}
    style={addMode === 'tunes' ? { backgroundColor: 'var(--text-primary)', color: 'var(--bg-card)' } : {}}
  >
    Tunes
  </button>
  <button
    className="btn-subtle"
    onClick={() => setAddMode('sets')}
    style={addMode === 'sets' ? { backgroundColor: 'var(--text-primary)', color: 'var(--bg-card)' } : {}}
  >
    Sets
  </button>
</div>
<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', maxWidth: '100%' }}>
<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', maxWidth: '600px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by title" style={inputStyle} />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search any field" style={inputStyle} />
        </div>
<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', maxWidth: '600px' }}>
          <select value={filterGenre} onChange={e => setFilterGenre(e.target.value)} style={inputStyle}>
            <option value="">All genres</option>
            {genreOptions.map((g, i) => <option key={i} value={g}>{g}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={inputStyle}>
            <option value="">All types</option>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {addMode === 'tunes' && (
            <select value={filterKey} onChange={e => setFilterKey(e.target.value)} style={inputStyle}>
              <option value="">All keys</option>
              {Object.keys(keyGroups).sort().map(root => (
                <optgroup key={root} label={root}>
                  <option value={`root:${root}`}>{root} (All Modes)</option>
                  {[...keyGroups[root]].sort().map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
        </div>
        <div style={{ border: 'none', borderRadius: '8px', padding: '6px', maxHeight: '200px', overflowY: 'auto', backgroundColor: 'var(--input-bg)' }}>
          {filtered.map(item => {
            const itemType = addMode === 'sets' ? 'set' : 'tune';
            const inSession = sessionItems.find(t => t.id === item.id && (t._entryType || 'tune') === itemType);
            return (
              <div key={item.id} onClick={() => {
                if (inSession) return;
            const newEntry = {
  id: item.id,
  title: item.title || '',
  type: item.type || '',
  key: item.key || '',
  _entryType: itemType,
  sessions: [{
    sessionId,
    location: location.trim() || 'Unnamed',
    startedAt: startedAt ? new Date(startedAt).toISOString() : new Date().toISOString()
  }]
};
setSessionItems(prev => [...prev, newEntry]);

                setIsDirty(true);
              }}
                style={{
                  padding: '4px 0',
                  fontWeight: inSession ? 'bold' : 'normal',
                  backgroundColor: inSession ? 'var(--button-bg, #f0f0f0)' : 'transparent',
                  color: inSession ? 'var(--text-muted, #999)' : 'inherit',
                  cursor: inSession ? 'default' : 'pointer'
                }}>
                <span className="text-scaled">
                  {item.title}{formatTuneLabel(item)}
                  {itemType === 'set' && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '0.68rem',
                      backgroundColor: getCollectionBadgeColor(item.mode || 'tunes'),
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {getCollectionBadge(item.mode || 'tunes')}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="save-cancel-row">
        <button className="btn-subtle btn-commit" onClick={handleSave}>Save Session</button>
        <button className="btn-subtle" onClick={async () => {
          const cleanup = async () => {
            if (wasCreatedEmpty && user && sessionId) {
              const ref = doc(db, 'users', user.uid, 'sessions', sessionId);
              try {
                if (navigator.onLine) {
                  await deleteDoc(ref);
                  await dexie.sessions.delete(sessionId);
                } else {
                  await dexie.sessions.delete(sessionId);
                  await addPendingWrite({
                    id: sessionId,
                    type: 'delete',
                    collection: 'sessions',
                    docId: sessionId
                  });
                }
              } catch (err) {
                console.error('Failed to delete empty session:', err);
              }
            }
            navigate('/sessions');
          };

          if (isDirty) {
            setPendingLeaveAction(() => cleanup);
            setShowLeaveModal(true);
          } else {
            await cleanup();
          }
        }}>Cancel</button>
      </div>

      <ConfirmLeaveModal open={showLeaveModal} onCancel={() => setShowLeaveModal(false)} onConfirm={() => {
        setShowLeaveModal(false);
        setIsDirty(false);
        if (pendingLeaveAction) pendingLeaveAction();
      }} />

      <ActionConfirmModal
        open={saveErrorModalOpen}
        title="Save Failed"
        message={saveErrorMessage}
        confirmLabel="OK"
        cancelLabel="Close"
        danger
        onConfirm={() => setSaveErrorModalOpen(false)}
        onCancel={() => setSaveErrorModalOpen(false)}
      />
    </PageWrapperAuto>
  );
}
